import {useState, useEffect, useLayoutEffect} from 'react';
import {useLocation} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';

import {api} from '../utils';
import {RootState, actionCreator} from '../_modules';
import {GeoJsonFeature, SearchResult} from '../schema';

import {Handlers, Store, State} from './types';
import {addEmTag, filterResults, score} from './utils';
import View from './view';


const useWindowWidth = (): number => {
  const [size, setSize] = useState<number>(0);
  useLayoutEffect(() => {
    const updateSize = (): void => {
      setSize(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
};


const CSearchResult: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [state, setState] = useState<State>({isLoaging: true, keyword: '', height: undefined});
  const windowWidth = useWindowWidth();

  const store: Store = useSelector((rootState: RootState): Store => ({
    results: rootState.searchResults,
    gjfs: rootState.gjfs.all,
  }));

  const handlers: Handlers = {
    handleClickArticle: (activeKey) => {
      dispatch(actionCreator.setActiveArticle({key: activeKey}));
    },
    focus: () => {
      const lngSet = store.gjfs.map((each) => each.geometry.coordinates[0]);
      const latSet = store.gjfs.map((each) => each.geometry.coordinates[1]);
      const minLng = Math.min(...lngSet);
      const minLat = Math.min(...latSet);
      const maxLng = Math.max(...lngSet);
      const maxLat = Math.max(...latSet);
      dispatch(actionCreator.setMapBounds({maxLngLat: [maxLng, maxLat], minLngLat: [minLng, minLat]}));
    },
  };

  useLayoutEffect(() => {
    const initZoom = window.innerWidth > 1280 ? 2 : window.innerWidth > 640 ? 1 : 0;
    const height = 250 * 2 ** (initZoom);
    setState({...state, height});
  }, [windowWidth]);

  useEffect(() => {
    dispatch(actionCreator.setSearchResults({results: []}));
    const urlParams = new URLSearchParams(location.search);
    const query = urlParams.get('q') || '';
    if (query === '') return;
    setState({...state, keyword: query, isLoaging: true});
    const searchType = urlParams.get('t') as 'd' | 'r' | 'k';
    api.post('/api/solr', {text: query, tgt: 'REKIHAKU'})
        .then((value) => {
          const rawResults = value.data as SearchResult[];
          let results: SearchResult[] = rawResults.map((each) => ({...each, solrScore: each.score}));
          switch (searchType) {
            case 'd':
              const lng = parseFloat(urlParams.get('x') || '');
              const lat = parseFloat(urlParams.get('y') || '');
              if (!isNaN(lng) && !isNaN(lat)) {
                dispatch(actionCreator.pointOnMap({lnglat: [lng, lat]}));
              }
              results = results.map((each) => {
                return score(each, [lng, lat]);
              });
              break;
            case 'r':
              const lngRangeString = urlParams.get('x')?.split(',') as [string, string];
              const lngRange = lngRangeString.map((each) => parseFloat(each)) as [number, number];
              const latRangeString = urlParams.get('y')?.split(',') as [string, string];
              const latRange = latRangeString.map((each) => parseFloat(each)) as [number, number];
              results = filterResults(results, lngRange, latRange);
              break;
            case 'k':
              results = results.map((each) => ({...each, text: addEmTag(each)}));
              break;
          }
          dispatch(actionCreator.setSearchResults({
            results: results.sort((a, b) => b.score - a.score),
          }));

          const geojsonFeatures = results.reduce((a: GeoJsonFeature[], b, key) => [
            ...a,
            ...b.geojson.map((each) => {
              return {...each, properties: {...each.properties, article: key}};
            }),
          ], []);
          dispatch(actionCreator.setGJFs({target: 'all', features: geojsonFeatures}));

          setState((state) => ({...state, isLoaging: false}));
        })
        .catch((e) => console.error(e));
  }, [location, dispatch]);

  return <View {...{handlers, state, store}}/>;
};

export default CSearchResult;
